/**
 * Edit Tenant: change the EDITION of a product this tenant already holds.
 *
 * Same model as Create Tenant — the same picker, fed by the same server rows —
 * but applied to a live tenant through its own endpoint:
 *
 *   GET /api/admin/tenants/{id}/product-editions          what can be changed
 *   PUT /api/admin/tenants/{id}/product-editions/{product} {edition}
 *
 * Kept OUT of the Edit Tenant form's Save on purpose. That Save PATCHes profile
 * fields; an edition change moves an entitlement and (for a downgrade) pauses
 * the tenant's automations on the platform. It is its own confirmed action, so
 * renaming a tenant can never downgrade it by accident.
 *
 * Upgrade Basic -> Automation enables the capability and touches no data.
 * Downgrade shows the server's own words ("Active automations will pause.
 * Nothing is deleted.") BEFORE the operator applies it.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../../components/ui/button';
import EditionPicker from './EditionPicker';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_API = `${BACKEND_URL}/api/admin`;

function errorText(e) {
  const d = e?.response?.data?.detail;
  return typeof d === 'string' ? d : 'The edition could not be changed.';
}

export default function ProductEditionSection({ tenantId, headers, onChanged }) {
  const [products, setProducts] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [choice, setChoice] = useState({});
  const [saving, setSaving] = useState(null);
  const [result, setResult] = useState({});
  const [error, setError] = useState({});

  useEffect(() => {
    let live = true;
    axios.get(`${ADMIN_API}/tenants/${tenantId}/product-editions`, { headers })
      .then(({ data }) => { if (live) setProducts(data.products || []); })
      .catch((e) => { if (live) setLoadError(errorText(e)); });
    return () => { live = false; };
    // `headers` is rebuilt every render by the page; the tenant is the key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  if (loadError) {
    return (
      <p className="text-xs text-warning-text" data-testid="product-editions-error">
        Editions could not be loaded: {loadError}
      </p>
    );
  }
  if (!products || products.length === 0) return null;

  const apply = async (p) => {
    const edition = choice[p.product];
    setSaving(p.product);
    setError((x) => ({ ...x, [p.product]: null }));
    try {
      const { data } = await axios.put(
        `${ADMIN_API}/tenants/${tenantId}/product-editions/${p.product}`,
        { edition }, { headers });
      setProducts((list) => list.map((x) => (x.product === p.product
        ? { ...x, current_edition: data.edition } : x)));
      setChoice((c) => ({ ...c, [p.product]: undefined }));
      setResult((r) => ({ ...r, [p.product]: data }));
      onChanged?.(data);
    } catch (e) {
      setError((x) => ({ ...x, [p.product]: errorText(e) }));
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="rounded-lg border border-border px-3 py-3 space-y-3"
         data-testid="product-editions-section">
      <p className="text-sm font-medium">Editions</p>
      {products.map((p) => {
        const pending = choice[p.product];
        const dirty = pending != null && pending !== p.current_edition;
        const done = result[p.product];
        return (
          <div key={p.product} data-testid={`product-edition-${p.product}`}>
            <p className="text-sm">{p.label}</p>
            <EditionPicker
              code={p.product}
              row={p}
              current={p.current_edition}
              value={pending}
              disabled={saving === p.product}
              onChange={(ed) => setChoice((c) => ({ ...c, [p.product]: ed }))}
            />
            <div className="mt-2 pl-6 flex items-center gap-2">
              <Button type="button" size="sm" variant="outline"
                      disabled={!dirty || saving === p.product}
                      onClick={() => apply(p)}
                      data-testid={`apply-edition-${p.product}`}>
                {saving === p.product ? 'Changing…' : 'Change edition'}
              </Button>
              {done?.changed && (
                <span className="text-xs text-muted-foreground"
                      data-testid={`edition-result-${p.product}`}>
                  {done.message}
                  {done.agreement_amendment_required
                    ? ' The agreement is not re-priced automatically; amend it to bill the new edition.'
                    : ''}
                </span>
              )}
            </div>
            {error[p.product] && (
              <p className="mt-1 pl-6 text-xs text-warning-text" role="alert"
                 data-testid={`edition-error-${p.product}`}>
                {error[p.product]}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
